/**
 * PaymentTable Component
 * Displays client payments with sorting, status/method badges, and inline
 * process/fail actions for pending payments.
 */

import {
  ChevronUp,
  ChevronDown,
  CreditCard,
  CheckCircle2,
  XCircle,
  RefreshCw,
} from 'lucide-react'
import type { Payment, PaymentStatus } from '@/types/payment.types'
import {
  PAYMENT_STATUS_LABELS,
  PAYMENT_METHOD_LABELS,
  PAYMENT_GATEWAY_LABELS,
} from '@/types/payment.types'
import { usePaymentStore } from '@/store/paymentStore'

interface PaymentTableProps {
  payments: Payment[]
  /** Process a pending/processing payment (asks for a transaction id). */
  onProcess?: (payment: Payment) => void
  /** Fail a pending/processing payment (asks for a reason). */
  onFail?: (payment: Payment) => void
  /** Refresh a provider-managed payment from Razorpay. */
  onReconcile?: (payment: Payment) => void
}

const STATUS_STYLES: Record<PaymentStatus, string> = {
  completed: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  processing: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  pending: 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-200',
  failed: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  cancelled: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400',
  refunded: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
}

function formatAmount(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(amount)
  } catch {
    return `${currency} ${amount.toLocaleString('en-IN')}`
  }
}

function formatDate(dateStr: string): string {
  if (!dateStr) return '—'
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

export default function PaymentTable({ payments, onProcess, onFail, onReconcile }: PaymentTableProps) {
  const { sortBy, sortOrder, setSortBy, setSortOrder } = usePaymentStore()

  const handleSort = (column: typeof sortBy) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(column)
      setSortOrder('desc')
    }
  }

  const SortIcon = ({ column }: { column: typeof sortBy }) => {
    if (sortBy !== column) return null
    return sortOrder === 'asc' ? (
      <ChevronUp className="w-4 h-4 inline ml-1" />
    ) : (
      <ChevronDown className="w-4 h-4 inline ml-1" />
    )
  }

  const isActionable = (status: PaymentStatus) =>
    status === 'pending' || status === 'processing'

  const hasVisibleAction = (payment: Payment) => isActionable(payment.status) && (
    (payment.gateway === 'manual' && Boolean(onProcess || onFail)) ||
    (payment.gateway === 'razorpay' && Boolean(onReconcile))
  )

  if (payments.length === 0) {
    return (
      <div className="card card-elevated p-12 text-center">
        <div className="w-16 h-16 bg-surface-overlay rounded-full flex items-center justify-center mx-auto mb-4">
          <CreditCard className="w-8 h-8 text-text-tertiary" />
        </div>
        <p className="text-lg font-medium text-text-primary mb-2">No payments found</p>
        <p className="text-text-secondary">Try adjusting your search or filters</p>
      </div>
    )
  }

  return (
    <div className="card card-elevated overflow-hidden">
      <div className="overflow-x-auto max-h-[70vh] overflow-y-auto">
        <table className="table-sticky-head w-full">
          <thead className="bg-surface-overlay">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-semibold text-text-primary">
                Customer
              </th>
              <th
                className="px-4 py-3 text-left text-sm font-semibold text-text-primary cursor-pointer hover:bg-surface-light transition-colors"
                onClick={() => handleSort('amount')}
              >
                Amount <SortIcon column="amount" />
              </th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-text-primary">
                Method
              </th>
              <th
                className="px-4 py-3 text-left text-sm font-semibold text-text-primary cursor-pointer hover:bg-surface-light transition-colors"
                onClick={() => handleSort('status')}
              >
                Status <SortIcon column="status" />
              </th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-text-primary">
                Transaction
              </th>
              <th
                className="px-4 py-3 text-left text-sm font-semibold text-text-primary cursor-pointer hover:bg-surface-light transition-colors"
                onClick={() => handleSort('createdAt')}
              >
                Created <SortIcon column="createdAt" />
              </th>
              <th className="px-4 py-3 text-right text-sm font-semibold text-text-primary">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-surface-overlay">
            {payments.map((payment) => (
              <tr
                key={payment.id}
                className="even:bg-surface-overlay/20 hover:bg-surface-overlay/50 transition-colors group"
              >
                <td className="px-4 py-4">
                  <p className="font-semibold text-text-primary">
                    {payment.customerName ?? `Customer #${payment.customerId}`}
                  </p>
                  {payment.description && (
                    <p className="text-sm text-text-secondary truncate max-w-64">
                      {payment.description}
                    </p>
                  )}
                </td>
                <td className="px-4 py-4">
                  <span className="font-medium text-text-primary">
                    {formatAmount(payment.amount, payment.currency)}
                  </span>
                </td>
                <td className="px-4 py-4">
                  <div className="text-sm">
                    <p className="text-text-primary">
                      {PAYMENT_METHOD_LABELS[payment.paymentMethod]}
                    </p>
                    <p className="text-text-tertiary text-xs">
                      {PAYMENT_GATEWAY_LABELS[payment.gateway]}
                    </p>
                  </div>
                </td>
                <td className="px-4 py-4">
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_STYLES[payment.status]}`}
                  >
                    {PAYMENT_STATUS_LABELS[payment.status]}
                  </span>
                  {payment.providerStatus && (
                    <p className="text-xs text-text-tertiary mt-1">Provider: {payment.providerStatus}</p>
                  )}
                </td>
                <td className="px-4 py-4">
                  {payment.transactionId ? (
                    <code className="font-mono text-xs bg-surface-overlay px-2 py-1 rounded">
                      {payment.transactionId}
                    </code>
                  ) : (
                    <span className="text-text-tertiary text-sm">—</span>
                  )}
                </td>
                <td className="px-4 py-4 text-sm text-text-secondary">
                  {formatDate(payment.createdAt)}
                </td>
                <td className="px-4 py-4">
                  <div className="flex items-center justify-end gap-1">
                    {payment.gateway === 'manual' && isActionable(payment.status) && onProcess && (
                      <button
                        onClick={() => onProcess(payment)}
                        className="btn btn-ghost btn-sm flex items-center gap-1 text-green-600 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-900/20"
                        title="Mark as completed"
                      >
                        <CheckCircle2 className="w-4 h-4" />
                        Process
                      </button>
                    )}
                    {payment.gateway === 'manual' && isActionable(payment.status) && onFail && (
                      <button
                        onClick={() => onFail(payment)}
                        className="btn btn-ghost btn-sm flex items-center gap-1 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                        title="Mark as failed"
                      >
                        <XCircle className="w-4 h-4" />
                        Fail
                      </button>
                    )}
                    {payment.gateway === 'razorpay' && isActionable(payment.status) && onReconcile && (
                      <button
                        onClick={() => onReconcile(payment)}
                        className="btn btn-ghost btn-sm flex items-center gap-1 text-teal-700 hover:bg-teal-50 dark:text-teal-300 dark:hover:bg-teal-900/20"
                        title="Refresh status from Razorpay"
                      >
                        <RefreshCw className="w-4 h-4" />
                        Refresh status
                      </button>
                    )}
                    {!hasVisibleAction(payment) && (
                      <span className="text-xs text-text-tertiary">—</span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
